import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { enUS, ru, uz } from 'date-fns/locale';

import { Calendar } from './ui/calendar';
import { Button } from './ui/button';
import { Drawer, DrawerContent, DrawerTitle } from './ui/drawer';
import { XIcon } from '../assets/X';

type DatePickerDrawerProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  title: string;
  value: Date;
  onChange: (value: Date) => void;
  /** Границы включительно; дни вне них недоступны. */
  min?: Date;
  max?: Date;
};

const LOCALES = { ru, uz, en: enUS } as const;

/**
 * Выбор даты в шторке — вместо системного пикера.
 *
 * Нативный `<input type="date">` в webview выглядит чужеродно и на Android
 * открывает материальный диалог, никак не связанный с интерфейсом. Здесь тот
 * же календарь, что и у даты рождения в профиле: зелёное выделение,
 * выпадающие месяц и год, язык приложения.
 *
 * Черновик хранится локально и уходит наружу только по «Готово» — тап по дню
 * не должен закрывать шторку и тем более дёргать запросы.
 */
export const DatePickerDrawer = ({
  open,
  setOpen,
  title,
  value,
  onChange,
  min,
  max,
}: DatePickerDrawerProps) => {
  const { t, i18n } = useTranslation();
  const [draft, setDraft] = useState<Date>(value);
  const locale =
    LOCALES[(i18n.language as keyof typeof LOCALES) || 'ru'] ?? ru;

  // При каждом открытии стартуем с текущего значения, а не с прошлого черновика.
  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const submit = () => {
    onChange(draft);
    setOpen(false);
  };

  return (
    <Drawer open={open} onOpenChange={setOpen} shouldScaleBackground={false}>
      {/* Открывается поверх другой шторки: второй плотный оверлей сделал бы
          фон чёрным, поэтому здесь лёгкий. */}
      <DrawerContent
        handleClassName="hidden"
        overlayClassName="bg-black/40"
        className="border-none bg-white"
      >
        <div className="flex items-center justify-between px-4 pb-2 pt-4">
          <DrawerTitle className="text-display-md text-text-primary">
            {title}
          </DrawerTitle>
          <button
            type="button"
            aria-label={t('cancel')}
            onClick={() => setOpen(false)}
            className="flex h-6 w-6 items-center justify-center rounded-full bg-[#e1e4e8]"
          >
            <XIcon className="h-2.5 w-2.5 text-text-icon-secondary" />
          </button>
        </div>

        <div className="flex justify-center px-4">
          <Calendar
            mode="single"
            selected={draft}
            defaultMonth={draft}
            onSelect={day => day && setDraft(day)}
            disabled={[
              ...(min ? [{ before: min }] : []),
              ...(max ? [{ after: max }] : []),
            ]}
            locale={locale}
            fromYear={min?.getFullYear() ?? new Date().getFullYear() - 5}
            toYear={max?.getFullYear() ?? new Date().getFullYear()}
            className="p-3"
          />
        </div>

        <div className="px-4 pb-safe-bottom pt-4">
          <Button
            onClick={submit}
            className="mb-4 h-12 w-full rounded-xl bg-button-primary shadow-none"
          >
            <p className="text-display-sm text-text-white">{t('doneButton')}</p>
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
